import React, { useState, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Staples from './pages/Staples';
import Statements from './pages/Statements';
import ProductDetail from './pages/ProductDetail';
import SignUp from './pages/SignUp';

import SplashScreen from './components/SplashScreen';
import Collections from './pages/Collections';
import Login from './pages/Login';
import Account from './pages/Account';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import Header from './components/Header';
import SubHeader from './components/SubHeader';
import SideMenu from './components/SideMenu';
import SortMenu from './components/SortMenu';
import Cart from './components/Cart';
import Media from './pages/Media';

// This component contains the main application layout and routing.
// It uses the useLocation hook, so it must be a child of Router.
const AppContent = () => {
  useLocation(); // Keep hook for potential future use
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isSortMenuOpen, setSortMenuOpen] = useState(false);
  const [isCartOpen, setCartOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('');

  const toggleMenu = () => {
    setMenuOpen(!isMenuOpen);
  };

  const toggleSortMenu = () => {
    setSortMenuOpen(!isSortMenuOpen);
  };

  const toggleCart = () => {
    setCartOpen(!isCartOpen);
  };

  const handleSortChange = (order) => {
    setSortOrder(order);
    console.log('Sort order changed to:', order); // For debugging
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const checkoutId = localStorage.getItem('checkoutId');
      if (checkoutId) {
        setCartOpen(true);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <>
      <Header onCartToggle={toggleCart} />
      <SubHeader onSortClick={toggleSortMenu} onMenuToggle={toggleMenu} />
      <SideMenu isOpen={isMenuOpen} onClose={toggleMenu} />
      <SortMenu isOpen={isSortMenuOpen} onClose={toggleSortMenu} onSortChange={handleSortChange} selectedSort={sortOrder} />
      <Cart isOpen={isCartOpen} onClose={toggleCart} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/product/:handle" element={<ProductDetail />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/shop" element={<Staples sortOrder={sortOrder} />} />
        <Route path="/collections" element={<Collections sortOrder={sortOrder} />} />
        <Route path="/media" element={<Media />} />
        <Route path="/staples" element={<Staples sortOrder={sortOrder} />} />
        <Route path="/statements" element={<Statements sortOrder={sortOrder} />} />
        <Route path="/login" element={<Login />} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
        <Route path="/order-confirmation/:orderId" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
      </Routes>
    </>
  );
};

// This is the root component of the application.
const App = () => {
  const [showSplash, setShowSplash] = useState(() => !sessionStorage.getItem('splashShown'));

  // CSP is now applied in index.js

  useEffect(() => {
    if (window.location.pathname !== '/') {
      sessionStorage.setItem('splashShown', 'true');
      setShowSplash(false);
    }
  }, []);

  const handleSplashFinish = () => {
    sessionStorage.setItem('splashShown', 'true');
    setShowSplash(false);
  };

  return (
    <AuthProvider>
      {showSplash ? <SplashScreen onFinish={handleSplashFinish} /> : <AppContent />}
    </AuthProvider>
  );
};

export default App;
